// React
import { useEffect, useState } from "react";

// Context
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";
// Helpers
import { normalizedAngle } from "@DataPages/components/SalesOperations/Tabs/SalesLog/SpeedometerChart";
import { roundUpToNearest500 } from "@DataPages/helpers/salesOperations/salesLog/utils";
// Types
import { DictionaryMap } from "@Common/interfaces/types";
import { IDynamicPVRInfo } from "@DataPages/types/salesOperations/salesLog/model";

// Constants
const INITIAL_ANGLE = 180;
const NEEDLE_LENGTH_FACTOR = 3;
const ANGLE_10 = 10;
const ANGLE_20 = 20;
const ANGLE_30 = 30;
const ANGLE_40 = 40;
const ANGLE_50 = 50;
const ANGLE_60 = 60;
const ANGLE_70 = 70;
const ANGLE_80 = 80;
const ANGLE_90 = 90;
const ANGLE_100 = 100;
const ANGLE_110 = 110;
const ANGLE_120 = 120;
const ANGLE_130 = 130;
const ANGLE_140 = 140;
const ANGLE_150 = 150;
const ANGLE_160 = 160;
const ANGLE_170 = 170;
const ANGLE_180 = 180;
const RADIAN = Math.PI / INITIAL_ANGLE;

type NeedleGradientAdjustmentType = {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
	[key: string]: number;
};

/****************************************** HELPERS ******************************************/
const getGradient = (angle: number, color: string) => {
	if (angle === ANGLE_90) {
		return `url(#verticalGradient_${color})`;
	} else if (angle === ANGLE_180) {
		return `url(#gradient2_${color})`;
	} else if (angle === 0) {
		return `url(#gradient3_${color})`;
	}
	return `url(#gradient_${color})`;
};

const getNeedleLineInfo = function (
	angle: number,
	iR: number,
	oR: number
): { needleLength: number; needleGradientAdjustment: NeedleGradientAdjustmentType; firstOpacity: number; secondOpacity: number } {
	const needleInfo: {
		needleLength: number;
		needleGradientAdjustment: NeedleGradientAdjustmentType;
		firstOpacity: number;
		secondOpacity: number;
	} = {
		needleLength: (iR + 2 * oR) / NEEDLE_LENGTH_FACTOR,
		needleGradientAdjustment: { x1: 0, y1: 0, x2: 1, y2: 1 },
		firstOpacity: 1,
		secondOpacity: 0,
	};

	if (angle >= ANGLE_80 && angle < ANGLE_100) {
		needleInfo.needleGradientAdjustment = angle <= ANGLE_90 ? { x1: 1, y1: 1, x2: 1, y2: 0 } : { x1: 0, y1: 0, x2: 0, y2: 1 };
		needleInfo.firstOpacity = 0;
		needleInfo.secondOpacity = 1;
	} else {
		adjustNeedleGradient(angle, needleInfo);
	}

	if (angle > ANGLE_90) {
		for (const point in needleInfo.needleGradientAdjustment) {
			needleInfo.needleGradientAdjustment[point] = needleInfo.needleGradientAdjustment[point] === 1 ? 0 : 1;
		}
	}

	return { ...needleInfo };
};

const adjustNeedleGradient = function (
	angle: number,
	needleInfo: { needleLength?: number; needleGradientAdjustment: DictionaryMap<number>; firstOpacity?: number; secondOpacity?: number }
) {
	const conditions = [
		[ANGLE_10, ANGLE_170],
		[ANGLE_20, ANGLE_160],
		[ANGLE_30, ANGLE_150],
		[ANGLE_40, ANGLE_140],
		[ANGLE_50, ANGLE_130],
		[ANGLE_60, ANGLE_120],
		[ANGLE_70, ANGLE_110],
		[ANGLE_80, ANGLE_100],
	];

	for (const [min, max] of conditions) {
		if (angle < min || angle >= max) {
			needleInfo.needleGradientAdjustment = { x1: 1, y1: 1, x2: 0, y2: 1 };
			break;
		}
	}
};

type AnimatedNeedleProps = {
	value: number;
	cx: number;
	cy: number;
	iR: number;
	oR: number;
	minValue: number;
	maxValue: number;
	color: string;
	dynamicPVRInfo: IDynamicPVRInfo;
};
const AnimatedNeedle = ({ value, cx, cy, iR, oR, minValue, maxValue, color, dynamicPVRInfo }: Readonly<AnimatedNeedleProps>) => {
	const [angle, setAngle] = useState(ANGLE_180); // Initialize the angle to 180 (which represents 0 in the gauge)

	const { shouldReCalculateAnalytics } = useSalesLogChart();
	const newMaxValue = shouldReCalculateAnalytics ? roundUpToNearest500(dynamicPVRInfo?.maxValue) : maxValue;

	// Use the angle from state in your needle rendering logic
	const { needleLength: length, needleGradientAdjustment, firstOpacity, secondOpacity } = getNeedleLineInfo(angle, iR, oR);

	const sin = Math.sin(-RADIAN * angle);
	const cos = Math.cos(-RADIAN * angle);
	const bigRadius = 9;
	const smallRadius = 4;
	const x0 = cx;
	const y0 = cy;
	const xp = x0 + length * cos; // Border of Pie Point
	const yp = y0 + length * sin; // Border of Pie Point

	const startX = cx + ((oR + iR) / 2 - (oR - iR) / 2) * Math.cos(-angle * RADIAN);
	const startY = cy + ((oR + iR) / 2 - (oR - iR) / 2) * Math.sin(-angle * RADIAN);

	const finalX = x0 + (length - ANGLE_40) * cos;
	const finalY = y0 + (length - ANGLE_40) * sin;

	useEffect(() => {
		const timeoutId = setInterval(() => {
			setAngle((ang) => ang - 2);
		}, 4);

		if (angle <= 0) {
			clearInterval(timeoutId);
		}
		if (value < minValue) {
			clearInterval(timeoutId);
			setAngle(ANGLE_180);
		}
		if (angle <= normalizedAngle(value, minValue, newMaxValue)) {
			clearInterval(timeoutId);
			if (normalizedAngle(value, minValue, newMaxValue) > ANGLE_180) {
				setAngle(ANGLE_180);
			} else {
				setAngle(normalizedAngle(value, minValue, newMaxValue));
			}
		}

		// Clean up the timeout if the component unmounts before it finishes
		return () => clearInterval(timeoutId);
	}); // No dependency so executes it every time the component is mounted

	useEffect(() => {
		setAngle(ANGLE_180);
	}, [value]);

	return (
		<>
			<circle cx={xp + 40} cy={yp + 15} r={bigRadius} fill={color} stroke="none" />
			<circle cx={xp + 40} cy={yp + 15} r={smallRadius} fill={"#FFFFFF"} stroke="none" />
			<defs>
				<linearGradient id={`verticalGradient_${color}`} x1="0" y1="0" x2="0" y2="100%">
					<stop offset="0%" style={{ stopColor: color, stopOpacity: 1 }} />
					<stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
				</linearGradient>
			</defs>
			<defs>
				<linearGradient id={`gradient_${color}`} {...needleGradientAdjustment}>
					<stop offset={"5%"} style={{ stopColor: color, stopOpacity: firstOpacity }} />
					<stop offset={"95%"} style={{ stopColor: color, stopOpacity: secondOpacity }} />
				</linearGradient>
			</defs>{" "}
			<defs>
				<linearGradient id={`gradient2_${color}`} x1={1} y1={0} x2={0} y2={0}>
					<stop offset={"5%"} style={{ stopColor: color, stopOpacity: 0 }} />
					<stop offset={"95%"} style={{ stopColor: color, stopOpacity: 1 }} />
				</linearGradient>
			</defs>
			<defs>
				<linearGradient id={`gradient3_${color}`} x1={0} y1={0} x2={1} y2={0}>
					<stop offset={"5%"} style={{ stopColor: color, stopOpacity: 0 }} />
					<stop offset={"95%"} style={{ stopColor: color, stopOpacity: 1 }} />
				</linearGradient>
			</defs>
			{angle === ANGLE_90 ? (
				<rect x={startX + 39} y={startY + 15} width="3" height={finalY - startY} fill={`url(#verticalGradient_${color})`} />
			) : (
				<line
					x1={startX + 41}
					y1={startY + 15}
					x2={finalX + 41}
					y2={finalY + 16}
					stroke={getGradient(angle, color)}
					strokeWidth="3"
				/>
			)}
		</>
	);
};

export default AnimatedNeedle;
